import { SuccessfullAddMetierDialog } from '../components/metier/successfullAddMetierDialog';
import { AddMetierDialog } from '../components/metier/addMetierDialog';
import MetierPageHeader from '../components/metier/metierPageHeader';
import MetierFiltration from '../components/metier/metierFiltration';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { useHasPermission } from '../hooks/useHasPermission';
import { Divider, Box, LinearProgress } from '@mui/material';
import MetierList from '../components/metier/metierList';
import MetierGrid from '../components/metier/metierGrid';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';
import '../css/roles/roles.css';

const Metier = () => {

    const hasMetierCreatePermission = useHasPermission('metier_create');

    const [openAddMetier, setOpenAddMetier] = useState(false);
    const [openSuccessfulAddMetier, setOpenSuccessfulAddMetier] = useState(false);

    const [loading, setLoading] = useState(true);
    const [metiers, setMetiers] = useState([])
    const [totalMetiers, setTotalMetiers] = useState('');
    const [filteredMetiersCount, setFilteredMetiersCount] = useState('');
    const [metiersFetched, setMetiersFetched] = useState(0);
    const [selectedDisplayMode, setSelectedDisplayMode] = useState(JSON.parse(localStorage.getItem('selectedMetiersDisplayMode')) || 'grid');
    const [metiersFilters, setMetiersFilters] = useState(
        JSON.parse(localStorage.getItem('metiersFilters')) ||
        { debouncedSearch: '' }
    );
    const defaultSortBy = {
        name: null,
        description: null,
    };
    
    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('metierSortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });
    
    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'metiers',
        pathname: '/dashboard/metiers',
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 280, list: 70 },
        resetOnFilters: metiersFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(metiersFetched);

    useEffect(() => {
        setLoading(true)
    }, [metiersFilters]);

    useEffect(() => {
        localStorage.setItem('metiersFilters', JSON.stringify(metiersFilters));
    }, [metiersFilters]);

    useEffect(() => {
        localStorage.setItem('metierSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem('selectedMetiersDisplayMode', JSON.stringify(selectedDisplayMode));
    }, [selectedDisplayMode]);

    const fetchMetiers = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('metier/list', { params: { offset: adjustedOffset, limit, filters: metiersFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedMetiers = [];
                // Update the fetched records in their correct positions
                response?.data?.metiers?.forEach((metier, index) => { updatedMetiers[adjustedOffset + index] = metier });
                // Update the state with the new metiers array
                setMetiers(updatedMetiers);
                setFilteredMetiersCount(response?.data?.filteredMetiersCount);
                setTotalMetiers(response?.data?.totalMetiers);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching metiers:', error);
            CustomErrorLogger(error, 'fetch metiers in metiers page');
        }
    }, [metiersFilters, offset, sortBy]);

    useEffect(() => {
        fetchMetiers()
    }, [fetchMetiers]);


    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Métiers" list={true}>
            <MetierPageHeader
                setOpenAddMetier={setOpenAddMetier}
                hasMetierCreatePermission={hasMetierCreatePermission}
            />
            <Divider />

            {loading ?
                <Box>
                    <MetierFiltration
                        filtersEmpty={filtersEmpty}
                        metiersFilters={metiersFilters}
                        setMetiersFilters={setMetiersFilters}
                        totalMetiers={totalMetiers}
                        metiersFetched={metiersFetched}
                        filteredMetiersCount={filteredMetiersCount}
                        selectedDisplayMode={selectedDisplayMode}
                        setSelectedDisplayMode={setSelectedDisplayMode}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalMetiers === 0 ?
                    <NoRecord
                        description="Aucun métier ajouté pour le moment"
                        buttonTitle="Ajouter un métier"
                        handleOpenDialog={() => setOpenAddMetier(true)}
                        hasPermission={hasMetierCreatePermission}
                    /> :
                    <Box>
                        <MetierFiltration
                            filtersEmpty={filtersEmpty}
                            metiersFilters={metiersFilters}
                            setMetiersFilters={setMetiersFilters}
                            totalMetiers={totalMetiers}
                            metiersFetched={metiersFetched}
                            filteredMetiersCount={filteredMetiersCount}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                        />

                        {selectedDisplayMode === 'grid' ? (
                            <MetierGrid
                                scrollRef={scrollRef}
                                metiers={metiers}
                                setOffset={setOffset}
                                filteredMetiersCount={filteredMetiersCount}
                                setMetiersFetched={setMetiersFetched}
                            />
                        ) : (
                            <MetierList
                                scrollRef={scrollRef}
                                metiers={metiers}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                setOffset={setOffset}
                                filteredMetiersCount={filteredMetiersCount}
                                setMetiersFetched={setMetiersFetched}
                            />
                        )}

                    </Box>
            }

            {openAddMetier &&
                <AddMetierDialog
                    setMetiers={setMetiers}
                    setTotalMetiers={setTotalMetiers}
                    setFilteredMetiersCount={setFilteredMetiersCount}
                    openAddMetier={openAddMetier}
                    setOpenAddMetier={setOpenAddMetier}
                />
            }

            {openSuccessfulAddMetier &&
                <SuccessfullAddMetierDialog
                    openSuccessfulAddMetier={openSuccessfulAddMetier}
                    setOpenAddMetier={setOpenAddMetier}
                />
            }

        </Page>
        </ListNavigationProvider>
    );
};

export default Metier;